import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';

import {
  DevTool,
  TOOL_HOTKEY_MAP,
} from '@/features/developer-tools/components/dev-tools.constants';
import {
  bringToFrontAtom,
  debugWindowOrderAtom,
} from '@/features/developer-tools/state/debug-window.state';
import {
  createPanelAtom,
  devToolsPanelInstancesAtom,
  removePanelAtom,
} from '@/features/developer-tools/state/dev-tools-panel.state';

/**
 * Hook that manages global hotkeys for the unified Developer Tools panel
 *
 * Hotkeys:
 * - Cmd/Ctrl+Shift+[0-5]: Open panel to specific tab (or switch tab if panel open)
 * - Escape: Close topmost panel
 * - Cmd/Ctrl+Shift+Escape: Close all panels
 */
export const useDevToolsHotkeys = () => {
  const instances = useAtomValue(devToolsPanelInstancesAtom);
  const windowOrder = useAtomValue(debugWindowOrderAtom);
  const createPanel = useSetAtom(createPanelAtom);
  const removePanel = useSetAtom(removePanelAtom);
  const bringToFront = useSetAtom(bringToFrontAtom);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isModifierKey = e.ctrlKey || e.metaKey;
      const isShiftKey = e.shiftKey;

      // Handle Cmd/Ctrl+Shift+Escape to close all panels
      if (isModifierKey && isShiftKey && e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        // Close all panels
        instances.forEach((instanceId) => {
          removePanel(instanceId);
        });
        return;
      }

      // Handle Escape to close topmost panel
      // Skip if focus is inside a CodeMirror panel (e.g., search bar) so Escape dismisses the panel first
      if (e.key === 'Escape' && !isModifierKey && !isShiftKey) {
        const target = e.target as HTMLElement;
        if (target.closest('.cm-panels')) {
          return;
        }

        // Find the topmost dev-tools panel
        const topmostPanelWindow = [...windowOrder]
          .reverse()
          .find((id) => id.startsWith('dev-tools-panel-'));

        if (topmostPanelWindow) {
          e.preventDefault();
          e.stopPropagation();
          const instanceId = topmostPanelWindow.replace('dev-tools-panel-', '');
          removePanel(instanceId);
        }
        return;
      }

      // Handle Cmd/Ctrl+Shift+[0-5] to open/switch tabs
      if (isModifierKey && isShiftKey) {
        // Get the key - handle both e.key and e.code for number keys
        const key = e.code.replace('Digit', '').replace('Key', '');
        const targetTab = TOOL_HOTKEY_MAP[key];

        if (targetTab) {
          e.preventDefault();
          e.stopPropagation();

          if (instances.length === 0) {
            // Create new panel and open to target tab
            createPanel(targetTab);
          } else {
            // Switch the topmost panel to the target tab and bring to front
            const topmostInstanceId = instances[instances.length - 1];
            const windowId = `dev-tools-panel-${topmostInstanceId}`;

            // Update the active tab for the topmost instance
            // We need to do this through the atom directly
            // This is handled by creating a new write operation
            bringToFront(windowId as `dev-tools-panel-${string}`);

            // The tab switching is handled by dispatching a custom event
            // that the panel component listens to
            window.dispatchEvent(
              new CustomEvent('dev-tools-switch-item', {
                detail: { instanceId: topmostInstanceId, tab: targetTab },
              })
            );
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => window.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [instances, windowOrder, createPanel, removePanel, bringToFront]);
};

/**
 * Hook to listen for tab switch events from hotkeys
 * Used by DevToolsPanel to respond to hotkey commands
 */
export const useDevToolsItemSwitchListener = (
  instanceId: string,
  onItemSwitch: (tab: DevTool) => void
) => {
  useEffect(() => {
    const handleItemSwitch = (e: CustomEvent<{ instanceId: string; tab: DevTool }>) => {
      if (e.detail.instanceId === instanceId) {
        onItemSwitch(e.detail.tab);
      }
    };

    window.addEventListener('dev-tools-switch-item', handleItemSwitch as EventListener);
    return () =>
      window.removeEventListener('dev-tools-switch-item', handleItemSwitch as EventListener);
  }, [instanceId, onItemSwitch]);
};
